import React from "react";

import { useLocation } from "react-router-dom";
import { isBrowser, isMobile } from "react-device-detect";

import { createAppTheme } from "./styles";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Box from "@mui/material/Box";

import MainPage from "./general/main/MainPage";
import PageLoader from "./helper-components/pages/PageLoader";
import ConfirmationDialog from "./helper-components/input/ConfirmationDialog";

import { getKeyFromQuery } from "./helper-functions/shared/pageUtils.js";
import { disableConsoleLogging } from "./helper-functions/shared/miscUtils.js";

import { useLocalStorage } from "./helper-functions/hooks/useLocalStorage";
import { useWindowDimensions } from "./helper-functions/hooks/useWindowDimensions";
import { useSnackbar } from "./helper-functions/hooks/useSnackbar";
import { useDialog } from "./helper-functions/hooks/useDialog";

import { useNavigateWithPrev } from "./helper-functions/hooks/useNavigation.js";
import { useBackendStates } from "./helper-functions/hooks/useBackendStates.js";
import { useDataGridPaginationState } from "./helper-functions/hooks/useDataGridPaginationState";
import { libraryColumnVisibility } from "./helper-functions/table/libraries";
import { transcriptColumnVisibility } from "./helper-functions/table/transcripts";

import { getLanguage } from "./config/language/language";
import * as Constants from "./config/constants/Constants.js";

export const ColorModeContext = React.createContext({
    toggleColorMode: () => {}
});

export default function App() {
    // Find URL parameter: debug.
    const debug = getKeyFromQuery("debug");

    // Variables can be saved in a browser's localstorage.
    const [mode, setMode] = useLocalStorage("colorMode", "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            }
        }),
        [setMode]
    );

    // The project's theme is here.
    // Can also modify all the MUI components.
    const theme = React.useMemo(() => createAppTheme(mode, isMobile), [mode]);

    // Console log some data.
    console.log(``);
    console.log(`==== ${getLanguage().WEBSITE_NAME} ====`);
    console.log(`Version: ${Constants.VERSION}`);
    console.log(`Running in ${isMobile ? "mobile" : "desktop"} view`);
    console.log(`Development mode: ${Constants.DEVELOPMENT_ENV}`);
    console.log(`================`);
    console.log(``);

    if (!Constants.DEVELOPMENT_ENV && debug !== "true") disableConsoleLogging();

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <StyledApp />
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

/**
 * Every single component visible to the user is inside this one.
 * This is the master-component, so to say.
 *
 * Define any variables (states) used throughout the app here.
 * All such variables will be passed to Main.js as props.
 * NB! The spread operator doesn't deep copy - use JSON parse and stringify.
 */
function StyledApp() {
    const location = useLocation();

    // Thanks to useState, <setVariable> changes <variable> dynamically.
    const [isPageLoading, setIsPageLoading] = React.useState(Constants.PAGE_ENABLE_LOADER);
    const [isConnectionToServerEstablished, setIsConnectionToServerEstablished] = React.useState(false);
    const [isLoggedIn, setIsLoggedIn] = React.useState(false); // Logged in depends on localstorage variables and JWT validity.

    const [showMainPageComponents, setShowMainPageComponents] = React.useState(true); // Allows to hide the main page components (e.g. sidebar).
    const [expand, setExpand] = React.useState(null); // Used to expand accordion(s).

    // Variables can be saved in a browser's localstorage.
    const [username, setUsername] = useLocalStorage("username", "");
    const [userJWT, setUserJWT] = useLocalStorage("jwt", "");

    // Tour guide is a step-by-step guide for new users.
    const [showInitialTourGuide, setShowInitialTourGuide] = useLocalStorage("initialTourGuide", true);
    const [runTourGuide, setRunTourGuide] = React.useState(false);
    const [tourGuideSteps, setTourGuideSteps] = React.useState([]);
    const [tourGuideStepIndex, setTourGuideStepIndex] = React.useState(0);

    // States queried from server (backend).
    const [me, setMe] = React.useState({});

    const backend = useBackendStates({ logOther: true });

    const libraries = useDataGridPaginationState("libraries", {
        logData: true,
        logPagination: true,
        initialSorting: { field: "created_on", sort: "desc" },
        initialVisibility: libraryColumnVisibility
    });
    const playlists = useDataGridPaginationState("playlists", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const resources = useDataGridPaginationState("resources", { logData: true, logPagination: true, initialSorting: { field: "uploaded_on", sort: "desc" } });
    const transcripts = useDataGridPaginationState("transcripts", {
        logData: true,
        logPagination: true,
        initialSorting: { field: "created_on", sort: "desc" },
        initialVisibility: transcriptColumnVisibility
    });
    const quotes = useDataGridPaginationState("quotes", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const authors = useDataGridPaginationState("authors", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const users = useDataGridPaginationState("users", { logData: true, logPagination: true, initialSorting: { field: "last_login", sort: "desc" } });
    const cookies = useDataGridPaginationState("cookies", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });

    const highlights = useDataGridPaginationState("highlights", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const highlightRequests = useDataGridPaginationState("highlight requests", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });

    const [highlightReviewQueue, setHighlightReviewQueue] = React.useState(null);
    const [recentYouTubeCookie, setRecentYouTubeCookie] = React.useState(null);
    const [recentVimeoCookie, setRecentVimeoCookie] = React.useState(null);

    const [showTranslations, setShowTranslations] = useLocalStorage("showTranslations", false);
    const textTranslations = useDataGridPaginationState("text translations", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const audioTranslations = useDataGridPaginationState("audio translations", { logData: true, logPagination: true, initialSorting: { field: "created_on", sort: "desc" } });
    const [audioPlayerTimestamps, setAudioPlayerTimestamps] = useLocalStorage("audioPlayerTimestamps", {});
    const [audioPlayerVolume, setAudioPlayerVolume] = useLocalStorage("audioPlayerVolume", 1);

    // Custom hooks.
    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigateWithPrev();
    const { enqueueDialog, setOpen, ...options } = useDialog();

    // Roles (depends on the JWT claim).
    const [isAdmin, setIsAdmin] = React.useState(false);

    // Search functionality.
    const [searchMode, setSearchMode] = useLocalStorage("searchMode", "SEMANTIC");

    const [keywordQuery, setKeywordQuery] = useLocalStorage("keywordQuery", "");
    const [keywordLimit, setKeywordLimit] = useLocalStorage("keywordLimit", 25);
    const [keywordThreshold, setKeywordThreshold] = useLocalStorage("keywordThreshold", 0.03);
    const [keywordTable, setKeywordTable] = useLocalStorage("keywordTable", "ALL");

    const [semanticQuery, setSemanticQuery] = useLocalStorage("semanticQuery", "");
    const [semanticLimit, setSemanticLimit] = useLocalStorage("semanticLimit", 10);
    const [semanticThreshold, setSemanticThreshold] = useLocalStorage("semanticThreshold", 0.15);
    const [semanticMaxResultsPerResource, setSemanticMaxResultsPerResource] = useLocalStorage("semanticMaxResultsPerResource", 5);
    const [semanticMinDurationBetweenResults, setSemanticMinDurationBetweenResults] = useLocalStorage("semanticMinDurationBetweenResults", 120);
    const [semanticLibraryIds, setSemanticLibraryIds] = useLocalStorage("semanticLibraryIds", []);

    const [keywordResult, setKeywordResult] = useLocalStorage("keywordResult", []);
    const [semanticResult, setSemanticResult] = useLocalStorage("semanticResult", "");

    const [semanticHistory, setSemanticHistory] = useLocalStorage("semanticHistory", []);

    // Menu sidebar (drawer).
    const { screenHeight, screenWidth } = useWindowDimensions();
    const [openAppDrawer, setOpenAppDrawer] = React.useState(screenWidth > Constants.AUTO_OPEN_SIDEBAR_PX);
    const toggleAppDrawer = () => {
        setOpenAppDrawer(!openAppDrawer);
    };

    // Config history sidebar.
    const [openNotificationDrawer, setOpenNotificationDrawer] = React.useState(false);
    const toggleNotificationDrawer = () => {
        setOpenNotificationDrawer(!openNotificationDrawer);
    };

    return (
        // Box is a general container where every other component is inside, kind of like a div.
        // This specifc box also deals with light/dark mode.
        <Box
            sx={{
                bgcolor: "background.default",
                color: "text.primary"
            }}>
            {/* Loading spinner. */}
            <PageLoader imageSrc="/images/ari_logo.png" show={isPageLoading} />

            {/* Actual page after loading in this div. */}
            <div hidden={isPageLoading}>
                {/* Dialog pop-up to confirm critical changes. */}
                <ConfirmationDialog
                    options={{
                        enqueueDialog,
                        setOpen,
                        ...options
                    }}
                />

                {/* Pass all props (variables) down to the child components. */}
                <MainPage
                    backend={{
                        ...backend,
                        // Database tables
                        libraries,
                        playlists,
                        resources,
                        transcripts,
                        quotes,
                        authors,
                        users,
                        // Translations
                        textTranslations,
                        audioTranslations,
                        // Highlights
                        highlights,
                        highlightRequests,
                        highlightReviewQueue,
                        setHighlightReviewQueue,
                        // Admin
                        cookies,
                        recentCookies: {
                            youtube: recentYouTubeCookie,
                            setYoutube: setRecentYouTubeCookie,
                            vimeo: recentVimeoCookie,
                            setVimeo: setRecentVimeoCookie
                        }
                    }}
                    search={{
                        // Search functionality.
                        mode: searchMode,
                        setMode: setSearchMode,
                        // Keyword search settings.
                        keywordQuery,
                        setKeywordQuery,
                        keywordLimit,
                        setKeywordLimit,
                        keywordThreshold,
                        setKeywordThreshold,
                        keywordTable,
                        setKeywordTable,
                        // Semantic search settings.
                        semanticQuery,
                        setSemanticQuery,
                        semanticLimit,
                        setSemanticLimit,
                        semanticThreshold,
                        setSemanticThreshold,
                        semanticMaxResultsPerResource,
                        setSemanticMaxResultsPerResource,
                        semanticMinDurationBetweenResults,
                        setSemanticMinDurationBetweenResults,
                        semanticLibraryIds,
                        setSemanticLibraryIds,
                        // Search results.
                        keywordResult,
                        setKeywordResult,
                        semanticResult,
                        setSemanticResult,
                        semanticHistory,
                        setSemanticHistory
                    }}
                    // User info.
                    user={{
                        me,
                        setMe,
                        username,
                        setUsername,
                        userJWT,
                        setUserJWT,
                        isLoggedIn,
                        setIsLoggedIn
                    }}
                    roles={{
                        isAdmin,
                        setIsAdmin
                    }}
                    browser={{
                        // Browser info.
                        navigate,
                        location,
                        screenHeight,
                        screenWidth,
                        isBrowser,
                        isMobile,
                        // Loading state.
                        isPageLoading,
                        setIsPageLoading,
                        showMainPageComponents,
                        setShowMainPageComponents,
                        expand,
                        setExpand,
                        // Tour guide.
                        showInitialTourGuide,
                        setShowInitialTourGuide,
                        runTourGuide,
                        setRunTourGuide,
                        tourGuideSteps,
                        setTourGuideSteps,
                        tourGuideStepIndex,
                        setTourGuideStepIndex,
                        // Translations.
                        showTranslations,
                        setShowTranslations,
                        // Audio player.
                        audioPlayerTimestamps,
                        setAudioPlayerTimestamps,
                        audioPlayerVolume,
                        setAudioPlayerVolume
                    }}
                    server={{
                        isConnectionToServerEstablished,
                        setIsConnectionToServerEstablished
                    }}
                    // Snackbar.
                    showSnackbar={showSnackbar}
                    // Confirmation dialog.
                    showDialog={enqueueDialog}
                    setOpenDialog={setOpen}
                    // Sidebar.
                    sidebar={{
                        openAppDrawer,
                        toggleAppDrawer,
                        openNotificationDrawer,
                        toggleNotificationDrawer
                    }}
                />
            </div>
        </Box>
    );
}
